// @ts-strict-ignore
import { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'src/store/store';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Element, Section } from 'src/constants/footfall';
import RelativeTime from 'src/components/relativeTime';
import UserLink from 'src/modules/shared/links/userLink';
import Badge from 'src/components/badge';
import PostLink from 'src/modules/shared/links/postLink';
import { stripBBTags } from 'src/utils/string';
import i18n from 'src/utils/translate';
import { titleMedium } from 'styles-js/mixins/typography';
import { showSignupModal } from 'src/modules/shared/modal/actions';
import Icon from 'src/components/icon';
import { LinkButton } from 'styles-js/buttons';
import { SeeMoreResults } from 'styles-js/resultsList';
import { Size } from 'src/components/avatar';

const LIMIT = 5;

export default function NextPosts(): JSX.Element {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);

  const { slug: communitySlug, isArchived } = useSelector(({ context }) => context.currentCommunity);
  const currentUser = useSelector(({ context }) => context.currentUser);

  const { nextPosts } = useNextAndRelatedPosts();

  if (nextPosts.length === 0) return null;

  const onClick = (postId) => {
    const postIds = nextPosts.map(post => post.id);
    trackFootfallEvent(Event.ClickedNextPost, {
      postIds,
      postId,
    });
  };

  const postsToRender = isExpanded ? nextPosts : nextPosts.slice(0, LIMIT);
  return (
    <>
      <Header>{currentUser ? i18n.t('Next posts') : i18n.t('You may also like...')}</Header>
      <Container>
        {postsToRender.map(({ author, title, id, urlEncodedTitle, totalResponses, snippet, dateCreated, postTypeId }) => {
          const isVisitorNoRepliesLink = !currentUser && !totalResponses;
          return (
            <div className="results-post" key={id}>
              <h3 className="results-post__title">
                <PostLink
                  communitySlug={communitySlug}
                  postId={id}
                  postTypeId={postTypeId}
                  urlEncodedTitle={urlEncodedTitle}
                  onClick={() => onClick(id)}
                >
                  {title}
                </PostLink>
              </h3>
              <div className="results-post__body">
                <CleanSnippet snippet={snippet}/>
              </div>
              <div className="results-post__footer" >
                <div className="results-post__footer__avatar">
                  <UserLink
                    avatarSize={Size.Small}
                    signupContext={{
                      element: Element.Avatar,
                      section: Section.NextPosts,
                    }}
                    user={author}
                  />
                </div>
                <div className="results-post__footer__content">
                  <div className="results-post__footer__content-title">
                    <UserLink
                      signupContext={{
                        element: Element.UserLink,
                        section: Section.NextPosts,
                      }}
                      user={author}
                    />
                    {author?.roles?.length > 0 && <StyledBadge>{author.roles[0]}</StyledBadge>}
                    <span className="results-post__footer__content-separator">•</span>
                    <RelativeTime rawTime={dateCreated} />
                  </div>
                  <PostLink
                    anchorToResponses={true}
                    communitySlug={communitySlug}
                    disabled={isVisitorNoRepliesLink}
                    postId={id}
                    postTypeId={postTypeId}
                    urlEncodedTitle={urlEncodedTitle}
                    onClick={(e) => {
                      onClick(id);
                      if (isVisitorNoRepliesLink) {
                        if (e) e.preventDefault();
                        dispatch(showSignupModal());
                      }
                    }}
                  >
                    {totalResponses > 0 ? (
                      <span className="results-post__footer__content-replies">
                        {totalResponses} {totalResponses === 1 ? i18n.t('Reply') : i18n.t('Replies')}
                      </span>
                    ) : <>{!isArchived && i18n.t('Be the first to reply')}</>
                    }
                  </PostLink>
                </div>
              </div>
            </div>
          );
        })}
        <SeeMoreResults>
          <LinkButton onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? i18n.t('Show fewer posts') : i18n.t('View more posts')} <Icon icon={isExpanded ? 'open-up' : 'open-down'} />
          </LinkButton>
        </SeeMoreResults>
      </Container>
    </>
  );
}

function CleanSnippet({ snippet }: { snippet: string }) {
  const clean = useMemo(() => snippet && stripBBTags(snippet), [snippet]);
  return (
    <>{clean}</>
  );
}

export const useNextAndRelatedPosts = () => {
  const { currentUser } = useSelector(({ context }) => context);
  const { relatedPosts, nextPosts } = useSelector(({ pages }) => pages.singlePost);

  if (currentUser && nextPosts?.length > 0) {
    return { nextPosts, relatedPosts };
  } else {
    return { nextPosts: relatedPosts, relatedPosts: relatedPosts.slice(5, 11) };
  }
};

const Container = styled.div`
  border: ${({ theme }) => theme.greyLine};
`;

const Header = styled.h2`
  ${titleMedium};
  padding: 35px 0 0 0;
  margin: 15px 0px;
`;

const StyledBadge = styled(Badge)`
  margin-right: 8px;
`;
